import React from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Task, FullProject } from '../../../types';

export interface SetupInboxListenersParams {
  INBOX_PROJECT_ID: string;
  tenantId: string;
  rawTasksRef: React.MutableRefObject<Task[]>;
  rawProjectsRef: React.MutableRefObject<FullProject[]>;
  mergeProjectsAndTasksRef: React.MutableRefObject<(() => void) | null>;
}

export function setupInboxListeners({
  INBOX_PROJECT_ID,
  tenantId,
  rawTasksRef,
  rawProjectsRef,
  mergeProjectsAndTasksRef,
}: SetupInboxListenersParams): (() => void)[] {
  const unsubscribers: (() => void)[] = [];

  const inboxDocRef = doc(db, 'projects', INBOX_PROJECT_ID);
  const unsubInbox = onSnapshot(
    inboxDocRef,
    { includeMetadataChanges: false },
    (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const tasksByProjectId = new Map<string, Task[]>();
        rawTasksRef.current.forEach((t) => {
          const pid = t.projectId || '';
          if (!tasksByProjectId.has(pid)) tasksByProjectId.set(pid, []);
          tasksByProjectId.get(pid)!.push(t);
        });

        const fp = {
          ...data,
          id: INBOX_PROJECT_ID,
          tasks: tasksByProjectId.get(INBOX_PROJECT_ID) || [],
        } as FullProject;

        const currentProjects = rawProjectsRef.current;
        const idx = currentProjects.findIndex((p) => p.id === fp.id);
        if (idx === -1) {
          rawProjectsRef.current = [...currentProjects, fp];
        } else {
          rawProjectsRef.current = currentProjects.map((p) => (p.id === fp.id ? fp : p));
        }
        if (mergeProjectsAndTasksRef.current) mergeProjectsAndTasksRef.current();
      }
    },
    (err) => {
      console.warn('[ProjectManager] Inbox listener error:', err);
    }
  );
  unsubscribers.push(unsubInbox);

  if (INBOX_PROJECT_ID !== 'inbox_inforum_' + tenantId) {
    const globalInboxDocRef = doc(db, 'projects', 'inbox_inforum_' + tenantId);
    const unsubGlobalInbox = onSnapshot(
      globalInboxDocRef,
      { includeMetadataChanges: false },
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const fp = {
            id: docSnap.id,
            name: data.name || 'Nicht zugeordnet',
            tasks: data.tasks || [],
            metadata: data.metadata || {},
            changelog: data.changelog || [],
            color: data.color || 'bg-slate-500',
            workspace: data.workspace || 'professional',
            archived: !!data.archived,
            order: data.order !== undefined ? data.order : undefined,
            lastModified: data.lastModified || Date.now(),
            categoryId: data.categoryId || undefined,
            userId: data.userId,
            sharedWith: data.sharedWith || [],
          } as FullProject;

          const currentProjects = rawProjectsRef.current;
          const idx = currentProjects.findIndex((p) => p.id === fp.id);
          if (idx === -1) {
            rawProjectsRef.current = [...currentProjects, fp];
          } else {
            rawProjectsRef.current = currentProjects.map((p) => (p.id === fp.id ? fp : p));
          }
          if (mergeProjectsAndTasksRef.current) mergeProjectsAndTasksRef.current();
        }
      },
      (err) => {
        console.warn('[ProjectManager] Global inbox listener error:', err);
      }
    );
    unsubscribers.push(unsubGlobalInbox);
  }

  return unsubscribers;
}
